import React, { useCallback, useMemo, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import { Button, Stack } from '@mui/material';
import { useUnit } from 'effector-react';
import { MRT_ColumnDef, MRT_SortingState } from 'material-react-table';
import { useSnackbar } from 'notistack';

import { useUserRoles } from './Form/queries';
import { useDeleteUser } from './queries';
import { UserDialog } from './UserDialog';
import { TableGridLocal } from '../../../components/TableGridLocal';
import { ConfirmationDialog } from '../../../components/UIKit/ConfirmationDialog';
import { InformationDialog } from '../../../components/UIKit/InformationDialog';
import { Strategies$ } from '../../../effector/strategies';
import { userInfoApi } from '../../../effector/userInfoList';
import { useUsers } from '../../../hooks/useUsers';
import { AggregateUser, UserType } from '../../../types/aggregateUser';
import { roleToText } from '../../../utils';

export const Users: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState<AggregateUser | null>(null);
  const [removeCandidate, setRemoveCandidate] = useState<{ limitation: boolean; candidate: AggregateUser } | null>(
    null,
  );

  useUserRoles();
  const { enqueueSnackbar } = useSnackbar();

  const { data, mutate: getUsers, isPending: isLoading, retryLast } = useUsers();
  const { mutateAsync: removeUser, isPending: isRemoveLoading } = useDeleteUser();

  const strategiesStore = useUnit(Strategies$);

  const handleAddUser = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpen(false);
    setRemoveCandidate(null);
    setUser(null);
  }, []);

  const handleUpdate = useCallback(() => {
    handleCloseDialog();
    retryLast();
  }, [retryLast, handleCloseDialog]);

  const handleRemoveUser = useCallback(async () => {
    const id = removeCandidate?.candidate.id as number;

    try {
      const result = await removeUser(id);

      if (result.errorMessage) {
        setError(result.errorMessage);
        return;
      }

      userInfoApi.remove(id);
      setRemoveCandidate(null);
      retryLast();
      enqueueSnackbar('Пользователь удален');
    } catch (e: unknown) {
      setError(String(e));
    }
  }, [enqueueSnackbar, removeCandidate?.candidate.id, removeUser, retryLast]);

  const strategies = useMemo(() => {
    if (!removeCandidate?.limitation && removeCandidate?.candidate.id) {
      return [];
    }

    return strategiesStore
      ?.filter(({ managerId }) => managerId === removeCandidate?.candidate.id)
      .map((s) => <li key={s.id}>{s.name}</li>);
  }, [removeCandidate?.candidate.id, removeCandidate?.limitation, strategiesStore]);

  const columns = useMemo<MRT_ColumnDef<AggregateUser>[]>(
    () => [
      {
        header: 'Идентификатор',
        accessorKey: 'login',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
      },
      {
        header: 'Организация',
        accessorKey: 'company',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
      },
      {
        header: 'Имя',
        accessorKey: 'displayName',
        filterVariant: 'text',
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
      },
      {
        header: 'Роль',
        accessorKey: 'role',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
        Cell: ({
          cell: {
            row: {
              original: { role },
            },
          },
        }) => <span>{roleToText(role)}</span>,
      },
      {
        header: '',
        accessorKey: 'id',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
        enableSorting: false,
        size: 40,
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return (
            <EditNoteRoundedIcon
              sx={{ cursor: 'pointer', marginTop: 0.5 }}
              onClick={() => {
                setOpen(true);
                setUser(original);
              }}
            />
          );
        },
      },
      {
        header: '',
        accessorKey: 'remove',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
        enableSorting: false,
        size: 40,
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          const hasStrategy =
            original.userType !== UserType.Representative
              ? strategiesStore.some(({ managerId }) => managerId === original.id)
              : false;

          return (
            <SwapHorizRoundedIcon
              sx={{ cursor: 'pointer', marginTop: 0.5 }}
              className="fall"
              onClick={() => {
                setRemoveCandidate({ limitation: hasStrategy, candidate: original });
              }}
            />
          );
        },
      },
    ],
    [strategiesStore],
  );

  const columnSorts = useMemo<MRT_SortingState>(
    () => [
      {
        desc: true,
        id: 'firstName',
      },
    ],
    [],
  );

  return (
    <>
      <Button variant="contained" color="primary" startIcon={<AddRoundedIcon />} onClick={handleAddUser}>
        Добавить
      </Button>
      <div style={{ marginTop: '30px' }}>
        <TableGridLocal
          name="administration-users"
          serverMode
          isLoading={isLoading}
          columns={columns}
          data={data?.result?.pageItems || []}
          pageSize={25}
          columnSorts={columnSorts}
          totalRowCount={data?.result?.total || 0}
          onFetchData={getUsers}
        />
      </div>

      {open && <UserDialog user={user} onClose={handleCloseDialog} onUpdate={handleUpdate} />}
      {removeCandidate && !removeCandidate.limitation && (
        <ConfirmationDialog
          title="Удаление пользователя"
          description={
            <Stack>
              <p>
                После удаления пользователь больше не сможет авторизоваться в expert.fintarget.ru со своим логином и
                паролем.
              </p>
              <p>
                Вы уверены, что хотите удалить пользователя <b>{removeCandidate.candidate.displayName}</b>?
              </p>
            </Stack>
          }
          isLoading={isRemoveLoading}
          error={error}
          onConfirm={handleRemoveUser}
          onClose={handleCloseDialog}
        />
      )}

      {removeCandidate && removeCandidate.limitation && (
        <InformationDialog
          title="Удаление пользователя"
          description={
            <Stack gap={2}>
              <p>
                Пользователь <b>{removeCandidate.candidate.displayName}</b> не может быть удален, так как является
                автором одной или нескольких стратегий.
              </p>
              <Stack gap={1}>
                <p>
                  Перед удалением пользователя необходимо изменить авторов указанных стратегий или удалить стратегии:
                </p>
                <ul>{strategies}</ul>
              </Stack>
            </Stack>
          }
          onClose={handleCloseDialog}
        />
      )}
    </>
  );
};
